import type { Language } from './roles';

/**
 * All user-facing text for bot and Mini App, in English and Amharic.
 * Amharic strings are a first draft and must be reviewed by a native speaker
 * before launch. Admin-editable content (welcome, warning, FAQ) will move to
 * the database in Phase 9; these remain the fallback.
 */
const en = {
  'lang.prompt': 'Please choose your language / እባክዎ ቋንቋ ይምረጡ',
  'lang.en': '🇬🇧 English',
  'lang.am': '🇪🇹 አማርኛ',
  'lang.saved': 'Language set to English.',

  'start.intro':
    'Welcome to Harmony! 💆\n\nFind trusted, verified massage providers near you, book a 60 or 120 minute session, and chat after your request is accepted.',
  'start.warning':
    '⚠️ Important\n\n• All providers are verified adults (18+).\n• Harmony is for professional massage services only.\n• Never send money outside the app for credits.\n• Report anyone who makes you feel unsafe.',
  'start.welcome_back': 'Welcome back, {name}!',
  'start.lets_start': "🚀 Let's Start",

  'menu.open': '📱 Open Harmony',
  'menu.how': 'ℹ️ How It Works',
  'menu.language': '🌐 Language',
  'menu.help': '🆘 Help',
  'menu.admin_dashboard': '📱 Admin Dashboard',
  'menu.title': 'Main menu',

  'how.text':
    '1. Open Harmony and create your profile.\n2. Browse providers nearest to you.\n3. Send a request for a 60 or 120 minute session.\n4. When the provider accepts, chat to arrange the meeting.\n5. Pay the provider in person and leave a rating.',
  'help.text': 'Need help? Just type your message here and our support team will reply in this chat.',

  'error.expired_button': 'This button has expired. Please use the menu again.',
  'error.generic': 'Something went wrong. Please try again.',

  // Mini App
  'app.loading': 'Loading…',
  'app.open_in_telegram': 'Please open Harmony from the Telegram bot.',
  'app.auth_failed': 'We could not verify your Telegram account. Please reopen the app from the bot.',
  'app.coming_soon': 'Coming soon',
  'app.account_suspended': 'Your Harmony account is suspended. Write to the bot if you think this is a mistake.',
  'app.continue': 'Continue',
  'app.choose_language': 'Choose your language',
  'app.welcome_title': 'Welcome to Harmony',
  'app.choose_role': 'How will you use Harmony?',
  'app.role_client': 'Find and book a massage near you',
  'app.role_provider': 'Offer massage sessions and get booked',

  'tab.home': 'Home',
  'tab.search': 'Search',
  'tab.chats': 'Chats',
  'tab.bookings': 'Bookings',
  'tab.profile': 'Profile',
  'tab.requests': 'Requests',
  'tab.otherProviders': 'Providers',
  'tab.myProviders': 'My Providers',
  'tab.agentRequests': 'Requests',
  'tab.dashboard': 'Dashboard',

  'profile.language': 'Language',
  'profile.role': 'Account type',
  'role.CLIENT': 'Client',
  'role.PROVIDER': 'Provider',
  'role.AGENT': 'Agent',
  'role.ADMIN': 'Admin',

  // Registration (Phase 2)
  'reg.client_title': 'Create your profile',
  'reg.provider_title': 'Become a provider',
  'reg.name': 'Name',
  'reg.name_hint': 'The name others will see',
  'reg.gender': 'Gender',
  'gender.MALE': 'Male',
  'gender.FEMALE': 'Female',
  'reg.phone': 'Phone number',
  'reg.share_phone': 'Share my Telegram number',
  'reg.phone_placeholder': '09XX XXX XXX',
  'reg.location': 'Location',
  'reg.use_my_location': 'Use my current location',
  'reg.location_saved': 'Current location saved',
  'reg.location_failed': 'Could not get your location. Choose your city instead.',
  'reg.or_choose_city': 'Or choose your city',
  'reg.city': 'City',
  'reg.next': 'Next',
  'reg.back': 'Back',
  'reg.skip': 'Skip',
  'reg.done': 'Done',
  'reg.edit': 'Edit',
  'reg.confirm_title': 'Check your details',
  'reg.client_success': 'Your profile is ready!',
  'reg.step_of': 'Step {n} of {total}',
  'reg.step_basic': 'About you',
  'reg.age': 'Age',
  'reg.age_hint': 'You must be 18 or older',
  'reg.step_prices': 'Your prices',
  'reg.price_for': '{minutes} minute session',
  'reg.price_hint': 'In birr. Leave empty if you do not offer it.',
  'reg.step_photos': 'Your photos',
  'reg.photos_hint': 'Add 1 to 3 clear photos of yourself.',
  'reg.add_photo': 'Add photo',
  'reg.uploading': 'Uploading…',
  'reg.step_location': 'Where do you work?',
  'reg.step_bio': 'Short bio',
  'reg.bio_hint': 'Up to 70 characters. Optional.',
  'reg.step_id': 'Verify your identity',
  'reg.id_photo': 'Photo of your ID',
  'reg.id_hint': 'Kebele ID, Fayda national ID, passport or driving licence.',
  'reg.selfie': 'Selfie holding your ID',
  'reg.selfie_hint': 'Your face and the ID must both be clearly visible.',
  'reg.id_private': 'Only Harmony admins see these. They are never shown to clients.',
  'reg.step_agent': 'Agent',
  'reg.agent_question': 'Do you have an agent? Enter their code.',
  'reg.agent_placeholder': 'AG-0000',
  'reg.agent_check': 'Check',
  'reg.agent_found': 'Agent: {name}',
  'reg.agent_from_link': 'You were invited by agent {name}.',
  'reg.agent_locked': 'Your agent: {name}',
  'reg.agent_none': 'No agent',
  'reg.step_confirm': 'Confirm and submit',
  'reg.submit': 'Submit for approval',
  'reg.bio': 'Bio',
  'reg.photos': 'Photos',
  'reg.id_docs': 'ID and selfie',
  'reg.provided': 'Provided',
  'reg.missing': 'Missing',
  'status.pending_title': 'Application sent',
  'status.pending_body': 'An admin will review your profile. You will get a Telegram message when it is approved.',
  'status.denied_title': 'Not approved yet',
  'status.denied_reason': 'Reason: {reason}',
  'status.reapply': 'Edit and apply again',
  'agent.your_code': 'Your agent code',
  'agent.your_link': 'Invite link for providers',
  'agent.share': 'Share link',
  'agent.copied': 'Copied',
  'err.REQUIRED': 'This field is required.',
  'err.INVALID_PHONE': 'Enter a valid Ethiopian mobile number.',
  'err.NAME_TOO_SHORT': 'Name is too short.',
  'err.NAME_TOO_LONG': 'Name is too long.',
  'err.UNDER_18': 'You must be 18 or older.',
  'err.PHOTO_REQUIRED': 'Add at least one photo.',
  'err.ID_PHOTO_REQUIRED': 'Add a photo of your ID.',
  'err.SELFIE_REQUIRED': 'Add a selfie holding your ID.',
  'err.PRICE_REQUIRED': 'Set a price for at least one session.',
  'err.LOCATION_REQUIRED': 'Share your location or choose your city.',
  'err.FILE_TOO_LARGE': 'This photo is too large.',
  'err.UNSUPPORTED_FILE_TYPE': 'Use a JPG, PNG or WebP photo.',
  'err.UNREADABLE_IMAGE': 'This photo could not be read. Try another.',
  'err.AGENT_NOT_FOUND': 'No agent with that code.',
  'err.TOO_MANY_UPLOADS': 'Too many uploads. Try again later.',

  // Notifications
  'notify.provider_approved': '✅ Your Harmony provider profile has been approved. You can now receive requests.',
  'notify.provider_denied': '❌ Your Harmony provider profile was not approved.\nReason: {reason}\n\nYou can edit your profile and apply again.',
  'notify.open_profile': '👤 Open Profile',
  'notify.reapply': '✏️ Edit and reapply',
  'notify.new_application': '🆕 New provider application from {name} is waiting for review.',
  'notify.open_admin': '📋 Review',

  // Agent invites (bot)
  'agent.claimed': '🎉 Welcome, {name}! You are now a Harmony agent.\n\nYour agent code: {code}\n\nShare this link with providers so they are linked to you when they register:\n{link}',
  'agent.claim_invalid': 'This agent invite link is invalid, expired or already used. Ask the Harmony admin for a new one.',
  'agent.claim_conflict': 'This Telegram account is already registered as a client or provider, so it cannot become an agent. Open the invite from another account or contact the admin.',
  'agent.already': 'You are already a Harmony agent. Your code: {code}',
  'test.reset_done': '🧪 Test reset done. Your role and profiles were removed. Send /start to register again.',
  'test.reset_admin': '🧪 Admin accounts cannot be reset. Use another Telegram account to test clients and providers.',

  // Common
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.close': 'Close',
  'common.retry': 'Try again',
  'common.load_more': 'Show more',
  'common.coming_soon': 'Coming soon',

  // Discovery (Phase 3)
  'feed.available_now': 'Available now',
  'feed.nearby': 'Near you',
  'feed.in_city': 'In your city',
  'feed.empty': 'No providers found. Try a wider distance or fewer filters.',
  'feed.no_location': 'Share your location in Profile to see who is closest.',
  'distance.under1': 'Under 1 km',
  'distance.km': '{km} km away',
  'filter.title': 'Filters',
  'filter.distance': 'Distance',
  'filter.any_distance': 'Any',
  'filter.gender': 'Gender',
  'filter.any': 'Any',
  'filter.session': 'Session',
  'filter.max_price': 'Max price (ETB)',
  'filter.rating': 'Rating',
  'filter.available': 'Available now',
  'filter.clear': 'Clear',
  'filter.apply': 'Show results',
  'filter.results': '{n} providers',
  'provider.from_price': 'from {price} ETB',
  'provider.new': 'New',
  'provider.book': 'Book',
  'provider.prices': 'Prices',
  'provider.about': 'About',
  'provider.report': 'Report',
  'provider.block': 'Block',
  'provider.unblock': 'Unblock',
  'provider.favorite_add': 'Add to favorites',
  'provider.favorite_remove': 'Remove from favorites',
  'provider.view_only': 'Only clients can book providers.',
  'provider.unavailable': 'Not available right now',
  'provider.available': 'Available',
  'provider.age': '{age} years',
  'provider.min': '{minutes} min',
  'report.title': 'Report {name}',
  'report.reason': 'What happened?',
  'report.details': 'Details (optional)',
  'report.send': 'Send report',
  'report.sent': 'Thank you. Our team will review it.',
  'report.cat.FAKE_PROFILE': 'Fake profile or photos',
  'report.cat.INAPPROPRIATE': 'Inappropriate behaviour',
  'report.cat.NO_SHOW': 'Did not show up',
  'report.cat.RUDE': 'Rude or disrespectful',
  'report.cat.SAFETY': 'I felt unsafe',
  'report.cat.SCAM': 'Asked for money outside the app',
  'report.cat.OTHER': 'Other',
  'block.confirm': 'Block {name}? You will not see them again.',
  'block.done': 'Blocked',
  'block.unblocked': 'Unblocked',
  'profile.favorites': 'Favorites',
  'profile.view_mine': 'See my profile as clients see it',
  'profile.blocked': 'Blocked providers',
  'profile.location': 'My location',
  'profile.update_location': 'Update my location',
  'profile.location_updated': 'Location updated',
  'favorites.empty': 'No favorites yet. Tap the heart on a provider to save them.',
  'blocked.empty': 'You have not blocked anyone.',
  'availability.label': 'Available for requests',
  'availability.on': 'Clients can see you and send requests.',
  'availability.off': 'You are hidden. Turn this on to get requests.',
  'availability.pending': 'Your profile is waiting for approval.',

  // Bookings (Phase 4)
  'book.title': 'Book {name}',
  'book.choose_session': 'Choose a session',
  'book.send': 'Send request',
  'book.sent_title': 'Request sent',
  'book.sent_body': '{name} has {expiry} minutes to answer. You will get a Telegram message when they reply.',
  'book.credits_left': 'Requests left: {n}',
  'book.no_credits_title': 'No requests left',
  'book.no_credits_body': 'You get {quota} free requests every {hours} hours. Next refill: {time}.',
  'book.recharge': 'Buy more requests',
  'book.recharge_soon': 'Buying requests is coming soon.',
  'book.keep_looking': 'Continue looking',
  'book.pay_in_person': 'You pay the provider in person when you meet.',
  'bookings.active': 'Active',
  'bookings.past': 'Past',
  'bookings.empty': 'No bookings yet. Find a provider on Home.',
  'status.PENDING': 'Waiting for reply',
  'status.ACCEPTED': 'Accepted',
  'status.DECLINED': 'Declined',
  'status.EXPIRED': 'Expired',
  'status.CANCELLED_BY_CLIENT': 'Cancelled',
  'status.CANCELLED_BY_PROVIDER': 'Cancelled by provider',
  'status.COMPLETED': 'Completed',
  'status.DISPUTED': 'Disputed',
  'status.CLOSED': 'Closed',
  'booking.cancel': 'Cancel',
  'booking.cancel_title': 'Why are you cancelling?',
  'cancel.PRICE': 'Price',
  'cancel.CHANGED_MIND': 'Changed my mind',
  'cancel.PROVIDER_CANCELED': 'Provider cancelled',
  'cancel.OTHER': 'Other',
  'booking.open_chat': 'Open chat',
  'booking.expires_in': 'Answer within {m} min',
  'booking.session': '{minutes} min · {price} ETB',
  'requests.empty': 'No requests yet.',
  'requests.new': 'New requests',
  'requests.accepted': 'Accepted',
  'requests.accept': 'Accept',
  'requests.decline': 'Decline',
  'err.NO_CREDITS': 'You have no requests left.',
  'err.PROVIDER_UNAVAILABLE': 'This provider is not available right now.',
  'err.ALREADY_REQUESTED': 'You already have a waiting request with this provider.',
  'err.REQUEST_EXPIRED': 'This request has expired.',
  'err.NOT_PENDING': 'This request was already answered.',
  'err.BLOCKED': 'You cannot book this provider.',
  'err.CLIENT_PROFILE_REQUIRED': 'Finish your profile first.',
  'err.SESSION_NOT_OFFERED': 'This provider does not offer that session length.',
  'err.NOT_FOUND': 'Not found. It may have been removed.',
  'notify.new_request': '🔔 {name} sent you a request: {minutes} min session ({price} ETB).\nPlease answer within {expiry} minutes.',
  'notify.open_requests': '📥 View request',
  'notify.request_accepted': '✅ {name} accepted your request! You can now chat to arrange the meeting.',
  'notify.request_declined': '❌ {name} declined your request.',
  'notify.request_expired': '⌛ Your request to {name} expired without a reply.',
  'notify.refund_line': 'Your request credit was returned.',
  'notify.cancelled_by_client': '🚫 {name} cancelled their request.',
  'notify.cancelled_by_provider': '🚫 {name} cancelled your booking.',
  'notify.open_chat': '💬 Open chat',
  'notify.open_bookings': '📋 My bookings',
  'notify.find_another': '🔍 Find another provider',

  // Chat (Phase 5)
  'chat.empty_list': 'No chats yet. A chat opens when a provider accepts a request.',
  'chat.placeholder': 'Message',
  'chat.photo': 'Photo',
  'chat.voice': 'Voice message',
  'chat.video': 'Video',
  'chat.recording': 'Recording… tap ■ to send',
  'chat.mic_denied': 'Microphone not available. Allow microphone access or send text.',
  'chat.closed': 'This chat is closed.',
  'chat.typing': 'typing…',
  'chat.failed': 'Not sent. Tap to retry.',
  'chat.start': 'Say hello and arrange where and when to meet.',
  'err.CHAT_CLOSED': 'This chat is closed.',
  'err.CHAT_DISABLED': 'Chat is turned off right now.',
  'err.MEDIA_DISABLED': 'Photos, voice and video are turned off right now.',
  'notify.msg.TEXT': '💬 New message from {name}',
  'notify.msg.PHOTO': '📷 {name} sent you a photo',
  'notify.msg.VOICE': '🎤 {name} sent you a voice message',
  'notify.msg.VIDEO': '🎥 {name} sent you a video',

  // Phases 6–9
  'profile.wallet': 'Wallet',
  'wallet.title': 'Wallet',
  'wallet.requests_left': 'Requests you can send',
  'wallet.free': 'Free',
  'wallet.paid': 'Bought',
  'wallet.refill': 'Free requests refill {time}',
  'wallet.buy': 'Buy requests',
  'wallet.history': 'History',
  'wallet.history_empty': 'No activity yet.',
  'wallet.payments': 'Payments',
  'ledger.CREDIT_PURCHASE': 'Bought requests',
  'ledger.CREDIT_SPEND': 'Sent a request',
  'ledger.CREDIT_RECHARGE': 'Free refill',
  'ledger.REFERRAL_REWARD': 'Invite reward',
  'ledger.ADMIN_ADJUSTMENT': 'Adjustment by Harmony',
  'ledger.REFUND': 'Request returned',
  'pay.choose_package': 'Choose a package',
  'pay.package_line': '{credits} requests',
  'pay.coupon': 'Coupon code',
  'pay.coupon_apply': 'Apply',
  'pay.coupon_ok': 'Coupon applied: −{amount} ETB',
  'pay.total': 'Total to pay',
  'pay.how_title': 'How to pay',
  'pay.instructions_default': 'Send the exact amount to the Harmony account shared by our team, then upload a screenshot of the receipt here.',
  'pay.upload': 'Upload payment screenshot',
  'pay.uploaded': 'Screenshot added',
  'pay.submit': 'Send for approval',
  'pay.sent_title': 'Payment sent',
  'pay.sent_body': 'We will check your payment and add the requests. You will get a Telegram message.',
  'pay.status.MANUAL_REVIEW': 'Checking',
  'pay.status.VERIFIED': 'Approved',
  'pay.status.DENIED': 'Not approved',
  'pay.status.PENDING': 'Waiting',
  'pay.status.INITIATED': 'Started',
  'pay.status.FAILED': 'Failed',
  'pay.status.EXPIRED': 'Expired',
  'pay.status.REFUNDED': 'Refunded',
  'pay.disabled': 'Buying requests is turned off right now.',
  'pay.no_packages': 'No packages are available yet.',
  'err.INVALID_COUPON': 'This coupon is not valid.',
  'err.COUPON_USED': 'You already used this coupon.',
  'err.PACKAGE_NOT_FOUND': 'This package is no longer available.',
  'err.EVIDENCE_REQUIRED': 'Add a screenshot of your payment.',
  'err.PAYMENT_DISABLED': 'Payments are turned off right now.',
  'err.TOO_MANY_PENDING': 'You already have payments waiting for approval.',
  'notify.payment_approved': '✅ Your payment was approved. {credits} requests were added to your wallet.',
  'notify.payment_denied': '❌ Your payment was not approved.\nReason: {reason}',
  'notify.subscription_paid': '✅ Your subscription payment was approved. Paid until {date}.',
  'notify.view_balance': '👛 View Balance',
  'notify.new_payment': '💳 New payment from {name}: {amount} ETB is waiting for approval.',
  'myprofile.preview': 'This is how clients see you',
  'myprofile.settings': 'Settings',
  'myprofile.inactive_by_agent': 'Your agent set you inactive. Clients cannot see you.',
  'myprofile.inactive_by_admin': 'Harmony set your profile inactive. Contact support.',
  'myprofile.inactive_self': 'Your profile is inactive. Clients cannot see you.',
  'myprofile.activate': 'Make me active',
  'myprofile.activated': 'You are active again',
  'myprofile.edit_requests': 'Suggested changes from your agent',
  'edit.field.displayName': 'Name',
  'edit.field.bio': 'Bio',
  'edit.field.prices': 'Prices',
  'edit.accept': 'Accept',
  'edit.reject': 'Reject',
  'edit.accepted': 'Changes saved',
  'edit.rejected': 'Suggestion rejected',
  'edit.current': 'Now',
  'edit.suggested': 'Suggested',
  'notify.agent_set_inactive': '⏸ Your agent {agent} set your profile to inactive. Clients cannot see you now. You can switch it back on in Profile.',
  'notify.agent_set_active': '▶️ Your agent {agent} set your profile to active again.',
  'notify.edit_suggested': '✏️ Your agent {agent} suggested changes to your profile. Open Profile to accept or reject them.',
  'notify.edit_accepted': '✅ {name} accepted your suggested changes.',
  'notify.edit_rejected': '❌ {name} rejected your suggested changes.',
  'notify.provider_reactivated': '▶️ {name} switched their profile back to active.',
  'notify.open_my_providers': '👥 My Providers',
  'agent.add_provider': 'Add a provider',
  'agent.add_hint': 'Providers enter your code when they register, or open your invite link.',
  'agent.no_providers': 'No providers yet. Share your code or link to add providers.',
  'agent.stat.active': 'Active providers',
  'agent.stat.received': 'Requests',
  'agent.stat.accepted': 'Accepted',
  'agent.stat.declined': 'Declined',
  'agent.stat.expired': 'Missed',
  'agent.stat.completed': 'Completed',
  'agent.stat.cancelled': 'Cancelled',
  'agent.stat.new': 'New providers',
  'agent.period.today': 'Today',
  'agent.period.week': 'This week',
  'agent.period.month': 'This month',
  'agent.period.last_month': 'Last month',
  'agent.pstatus.active': 'Active',
  'agent.pstatus.inactive': 'Inactive',
  'agent.pstatus.pending': 'Waiting for approval',
  'agent.pstatus.denied': 'Not approved',
  'agent.pstatus.draft': 'Still registering',
  'agent.set_active': 'Active (clients can see them)',
  'agent.suggest_edit': 'Suggest changes',
  'agent.suggest_send': 'Send suggestion',
  'agent.suggest_sent': 'Suggestion sent to {name}',
  'agent.suggest_pending': 'Waiting for {name} to answer your suggestion',
  'agent.remove': 'Remove from my list',
  'agent.remove_confirm': 'Remove {name} from your list? They stay linked to you; only an admin can change that.',
  'agent.removed': 'Removed from your list',
  'agent.requests_count': '{n} requests this month',
  'agent.subscription_title': 'Subscription',
  'agent.subscription_body': '{count} providers × {price} ETB = {amount} ETB every {days} days',
  'agent.subscription_pay': 'Pay now',
  'agent.subscription_paid': 'Paid until {date}',
  'agent.subscription_due': 'Payment due',
  'agent.subscription_pending': 'Payment waiting for approval',
  'agent.daily_report': '📊 Daily report — {date}\n\nActive providers: {active}\nRequests: {received}\nAccepted: {accepted}\nDeclined: {declined}\nMissed: {expired}\nCompleted: {completed}\nNew providers: {new}',
  'err.NOT_YOUR_PROVIDER': 'This provider is not on your list.',
  'err.EDIT_PENDING': 'You already have a suggestion waiting.',
  'err.NOTHING_TO_CHANGE': 'Change at least one thing.',
  'err.INACTIVE_BY_ADMIN': 'Only Harmony can switch this profile back on.',
  'fb.ask': '💆 How did your {minutes} min session with {name} go?',
  'fb.met': '✅ We met',
  'fb.not_yet': '⏳ Not met yet',
  'fb.canceled': '🚫 We canceled',
  'fb.rate': 'How would you rate {name}?',
  'fb.thanks': '🙏 Thank you for your rating!',
  'fb.ask_later': 'OK, we\'ll ask again later.',
  'fb.no_more': 'OK, we won\'t ask again.',
  'fb.cancel_reason': 'Why was it canceled?',
  'fb.canceled_ok': 'Thanks for letting us know.',
  'fb.already': 'You already answered this.',
  'fb.card_title': 'Did you meet {name}?',
  'fb.comment': 'Add a comment (optional)',
  'fb.send': 'Send rating',
  'notify.new_rating': '⭐ {name} rated your session {rating}/5.',
  'profile.invite': 'Invite friends',
  'invite.title': 'Invite friends',
  'invite.body': 'Share your link. When a friend joins and {event}, you get {credits} free requests.',
  'invite.event.REGISTERED': 'finishes registering',
  'invite.event.FIRST_BOOKING': 'their first request is accepted',
  'invite.share': 'Share invite link',
  'invite.copy': 'Copy link',
  'invite.joined': 'Friends joined',
  'invite.earned': 'Requests earned',
  'invite.stats': '{joined} joined · {rewarded} rewarded',
  'invite.share_text': 'Join me on Harmony — book trusted massage providers near you:',
  'invite.off': 'Invites are turned off right now.',
  'notify.referral_reward': '🎁 {name} joined with your invite. You got {credits} free requests!',
  'promo.use_code': 'Use code {code}',
  'tut.skip': 'Skip',
  'tut.next': 'Next',
  'tut.done': 'Get started',
  'checklist.title': 'Getting started',
  'checklist.progress': '{done} of {total} done',
  'profile.help': 'Help & support',
  'help.page_body': 'Write your question in the Harmony bot chat and our team will reply there.',
  'help.open_bot': 'Write to support',
  'help.faq': 'Questions & answers',
  'support.received': '📨 Thanks! Your message was sent to our support team. We will reply here.',
  'support.reply': '💬 Reply from Harmony support:\n\n{text}',
  'support.closed': '✅ Your support request was closed. Send a new message any time.',
  'notify.new_support': '💬 New support message from {name}:\n{text}',
  'admin.menu_title': '🛠 Admin menu',
  'admin.data': '📊 Data',
  'admin.issues': '🚩 Issues',
  'admin.broadcast': '📣 Broadcast',
  'admin.users': '👥 Manage Users',
  'admin.payments': '💳 Payments',
  'admin.support': '💬 Support',
  'admin.summary': '📊 Today\n\nNew users: {users}\nRequests: {requests}\nAccepted: {accepted}\nWaiting approvals: {approvals}\nOpen reports: {reports}\nPayments to check: {payments}\nSupport waiting: {support}',
  'notify.suspended': '⛔ Your Harmony account was suspended. Contact support if you think this is a mistake.',
  'notify.unsuspended': '✅ Your Harmony account is active again.',
  'notify.photos_changed': '📷 {name} changed their profile photos. Check they still match the ID.',
  'profile.edit': 'Edit profile',
  'edit.title': 'Edit profile',
  'edit.save': 'Save changes',
  'edit.saved': 'Profile saved',
  'edit.locked': 'Gender and age were checked against your ID. To change them, write to support.',
  'edit.photos_hint': '1 to 3 photos. The first one is shown first.',
} as const;

export type I18nKey = keyof typeof en;

const am: Record<I18nKey, string> = {
  'lang.prompt': 'Please choose your language / እባክዎ ቋንቋ ይምረጡ',
  'lang.en': '🇬🇧 English',
  'lang.am': '🇪🇹 አማርኛ',
  'lang.saved': 'ቋንቋ ወደ አማርኛ ተቀይሯል።',

  'start.intro':
    'እንኳን ወደ ሃርመኒ በደህና መጡ! 💆\n\nበአቅራቢያዎ ያሉ የተረጋገጡ የማሳጅ አገልግሎት ሰጪዎችን ያግኙ፣ የ60 ወይም 120 ደቂቃ ቀጠሮ ይያዙ፣ ጥያቄዎ ከተቀበለ በኋላ ይወያዩ።',
  'start.warning':
    '⚠️ አስፈላጊ መረጃ\n\n• ሁሉም አገልግሎት ሰጪዎች የተረጋገጡ ዕድሜያቸው 18+ የሆኑ ናቸው።\n• ሃርመኒ ለሙያዊ የማሳጅ አገልግሎት ብቻ ነው።\n• ለክሬዲት ከመተግበሪያው ውጭ ገንዘብ አይላኩ።\n• ደህንነት ያልተሰማዎትን ማንኛውንም ሰው ሪፖርት ያድርጉ።',
  'start.welcome_back': 'እንኳን ደህና ተመለሱ፣ {name}!',
  'start.lets_start': '🚀 እንጀምር',

  'menu.open': '📱 ሃርመኒን ክፈት',
  'menu.how': 'ℹ️ እንዴት ይሰራል',
  'menu.language': '🌐 ቋንቋ',
  'menu.help': '🆘 እርዳታ',
  'menu.admin_dashboard': '📱 የአስተዳዳሪ ዳሽቦርድ',
  'menu.title': 'ዋና ማውጫ',

  'how.text':
    '1. ሃርመኒን ከፍተው መገለጫዎን ይፍጠሩ።\n2. በአቅራቢያዎ ያሉ አገልግሎት ሰጪዎችን ይመልከቱ።\n3. ለ60 ወይም 120 ደቂቃ ቀጠሮ ጥያቄ ይላኩ።\n4. አገልግሎት ሰጪው ሲቀበል ስለ ቀጠሮው ይወያዩ።\n5. ለአገልግሎት ሰጪው በአካል ይክፈሉ እና ደረጃ ይስጡ።',
  'help.text': 'እርዳታ ይፈልጋሉ? መልዕክትዎን እዚህ ይላኩ፣ የድጋፍ ቡድናችን ምላሽ ይሰጣል።',

  'error.expired_button': 'ይህ ቁልፍ ጊዜው አልፎበታል። እባክዎ ማውጫውን እንደገና ይጠቀሙ።',
  'error.generic': 'ችግር ተፈጥሯል። እባክዎ እንደገና ይሞክሩ።',

  'app.loading': 'በመጫን ላይ…',
  'app.open_in_telegram': 'እባክዎ ሃርመኒን ከቴሌግራም ቦት ይክፈቱ።',
  'app.auth_failed': 'የቴሌግራም መለያዎን ማረጋገጥ አልቻልንም። እባክዎ መተግበሪያውን ከቦቱ እንደገና ይክፈቱ።',
  'app.account_suspended': 'የሃርመኒ መለያዎ ታግዷል። ስህተት ነው ብለው ካሰቡ ለቦቱ ይጻፉ።',
  'app.coming_soon': 'በቅርቡ ይመጣል',
  'app.continue': 'ቀጥል',
  'app.choose_language': 'ቋንቋ ይምረጡ',
  'app.welcome_title': 'እንኳን ወደ ሃርመኒ በደህና መጡ',
  'app.choose_role': 'ሃርመኒን እንዴት ይጠቀማሉ?',
  'app.role_client': 'በአቅራቢያዎ ማሳጅ ይፈልጉ እና ይያዙ',
  'app.role_provider': 'የማሳጅ አገልግሎት ይስጡ እና ቀጠሮ ያግኙ',

  'tab.home': 'መነሻ',
  'tab.search': 'ፈልግ',
  'tab.chats': 'መልዕክቶች',
  'tab.bookings': 'ቀጠሮዎች',
  'tab.profile': 'መገለጫ',
  'tab.requests': 'ጥያቄዎች',
  'tab.otherProviders': 'አገልግሎት ሰጪዎች',
  'tab.myProviders': 'የእኔ አገልግሎት ሰጪዎች',
  'tab.agentRequests': 'ጥያቄዎች',
  'tab.dashboard': 'ዳሽቦርድ',

  'profile.language': 'ቋንቋ',
  'profile.role': 'የመለያ አይነት',
  'role.CLIENT': 'ደንበኛ',
  'role.PROVIDER': 'አገልግሎት ሰጪ',
  'role.AGENT': 'ወኪል',
  'role.ADMIN': 'አስተዳዳሪ',

  'reg.client_title': 'መገለጫዎን ይፍጠሩ',
  'reg.provider_title': 'አገልግሎት ሰጪ ይሁኑ',
  'reg.name': 'ስም',
  'reg.name_hint': 'ሌሎች የሚያዩት ስም',
  'reg.gender': 'ጾታ',
  'gender.MALE': 'ወንድ',
  'gender.FEMALE': 'ሴት',
  'reg.phone': 'ስልክ ቁጥር',
  'reg.share_phone': 'የቴሌግራም ቁጥሬን አጋራ',
  'reg.phone_placeholder': '09XX XXX XXX',
  'reg.location': 'አድራሻ',
  'reg.use_my_location': 'አሁን ያለሁበትን ቦታ ተጠቀም',
  'reg.location_saved': 'ያሉበት ቦታ ተቀምጧል',
  'reg.location_failed': 'ቦታዎን ማግኘት አልተቻለም። እባክዎ ከተማዎን ይምረጡ።',
  'reg.or_choose_city': 'ወይም ከተማዎን ይምረጡ',
  'reg.city': 'ከተማ',
  'reg.next': 'ቀጣይ',
  'reg.back': 'ተመለስ',
  'reg.skip': 'ዝለል',
  'reg.done': 'ጨርሻለሁ',
  'reg.edit': 'አስተካክል',
  'reg.confirm_title': 'መረጃዎን ያረጋግጡ',
  'reg.client_success': 'መገለጫዎ ዝግጁ ነው!',
  'reg.step_of': 'ደረጃ {n} ከ {total}',
  'reg.step_basic': 'ስለ እርስዎ',
  'reg.age': 'ዕድሜ',
  'reg.age_hint': 'ዕድሜዎ 18 ወይም ከዚያ በላይ መሆን አለበት',
  'reg.step_prices': 'ዋጋዎችዎ',
  'reg.price_for': 'የ{minutes} ደቂቃ ቀጠሮ',
  'reg.price_hint': 'በብር። የማይሰጡት ከሆነ ባዶ ይተዉት።',
  'reg.step_photos': 'ፎቶዎችዎ',
  'reg.photos_hint': 'ከ1 እስከ 3 ግልጽ የሆኑ የራስዎን ፎቶዎች ያክሉ።',
  'reg.add_photo': 'ፎቶ ጨምር',
  'reg.uploading': 'በመጫን ላይ…',
  'reg.step_location': 'የት ነው የሚሰሩት?',
  'reg.step_bio': 'አጭር መግለጫ',
  'reg.bio_hint': 'እስከ 70 ፊደላት። አማራጭ ነው።',
  'reg.step_id': 'ማንነትዎን ያረጋግጡ',
  'reg.id_photo': 'የመታወቂያዎ ፎቶ',
  'reg.id_hint': 'የቀበሌ መታወቂያ፣ ፋይዳ ብሔራዊ መታወቂያ፣ ፓስፖርት ወይም መንጃ ፈቃድ።',
  'reg.selfie': 'መታወቂያዎን ይዘው የተነሱት ፎቶ',
  'reg.selfie_hint': 'ፊትዎም መታወቂያውም በግልጽ መታየት አለባቸው።',
  'reg.id_private': 'እነዚህን የሚያዩት የሃርመኒ አስተዳዳሪዎች ብቻ ናቸው። ለደንበኞች በፍጹም አይታዩም።',
  'reg.step_agent': 'ወኪል',
  'reg.agent_question': 'ወኪል አለዎት? የወኪሉን ኮድ ያስገቡ።',
  'reg.agent_placeholder': 'AG-0000',
  'reg.agent_check': 'አረጋግጥ',
  'reg.agent_found': 'ወኪል፦ {name}',
  'reg.agent_from_link': 'በወኪል {name} ተጋብዘዋል።',
  'reg.agent_locked': 'ወኪልዎ፦ {name}',
  'reg.agent_none': 'ወኪል የለም',
  'reg.step_confirm': 'አረጋግጠው ይላኩ',
  'reg.submit': 'ለማጽደቅ ላክ',
  'reg.bio': 'መግለጫ',
  'reg.photos': 'ፎቶዎች',
  'reg.id_docs': 'መታወቂያ እና ፎቶ',
  'reg.provided': 'ቀርቧል',
  'reg.missing': 'ይጎድላል',
  'status.pending_title': 'ማመልከቻዎ ተልኳል',
  'status.pending_body': 'አስተዳዳሪ መገለጫዎን ይገመግማል። ሲጸድቅ በቴሌግራም መልዕክት ይደርስዎታል።',
  'status.denied_title': 'ገና አልጸደቀም',
  'status.denied_reason': 'ምክንያት፦ {reason}',
  'status.reapply': 'አስተካክለው እንደገና ያመልክቱ',
  'agent.your_code': 'የወኪል ኮድዎ',
  'agent.your_link': 'ለአገልግሎት ሰጪዎች የግብዣ ሊንክ',
  'agent.share': 'ሊንኩን አጋራ',
  'agent.copied': 'ተቀድቷል',
  'err.REQUIRED': 'ይህ መሞላት አለበት።',
  'err.INVALID_PHONE': 'ትክክለኛ የኢትዮጵያ ሞባይል ቁጥር ያስገቡ።',
  'err.NAME_TOO_SHORT': 'ስሙ በጣም አጭር ነው።',
  'err.NAME_TOO_LONG': 'ስሙ በጣም ረጅም ነው።',
  'err.UNDER_18': 'ዕድሜዎ 18 ወይም ከዚያ በላይ መሆን አለበት።',
  'err.PHOTO_REQUIRED': 'ቢያንስ አንድ ፎቶ ያክሉ።',
  'err.ID_PHOTO_REQUIRED': 'የመታወቂያዎን ፎቶ ያክሉ።',
  'err.SELFIE_REQUIRED': 'መታወቂያዎን ይዘው የተነሱትን ፎቶ ያክሉ።',
  'err.PRICE_REQUIRED': 'ቢያንስ ለአንድ ቀጠሮ ዋጋ ያስገቡ።',
  'err.LOCATION_REQUIRED': 'ቦታዎን ያጋሩ ወይም ከተማዎን ይምረጡ።',
  'err.FILE_TOO_LARGE': 'ይህ ፎቶ በጣም ትልቅ ነው።',
  'err.UNSUPPORTED_FILE_TYPE': 'JPG፣ PNG ወይም WebP ፎቶ ይጠቀሙ።',
  'err.UNREADABLE_IMAGE': 'ይህን ፎቶ ማንበብ አልተቻለም። ሌላ ይሞክሩ።',
  'err.AGENT_NOT_FOUND': 'በዚህ ኮድ ወኪል አልተገኘም።',
  'err.TOO_MANY_UPLOADS': 'በጣም ብዙ ፎቶ ተጭኗል። ቆይተው ይሞክሩ።',

  'notify.provider_approved': '✅ የሃርመኒ አገልግሎት ሰጪ መገለጫዎ ጸድቋል። አሁን ጥያቄዎችን መቀበል ይችላሉ።',
  'notify.provider_denied': '❌ የሃርመኒ አገልግሎት ሰጪ መገለጫዎ አልጸደቀም።\nምክንያት፦ {reason}\n\nመገለጫዎን አስተካክለው እንደገና ማመልከት ይችላሉ።',
  'notify.open_profile': '👤 መገለጫ ክፈት',
  'notify.reapply': '✏️ አስተካክለህ እንደገና አመልክት',
  'notify.new_application': '🆕 ከ{name} አዲስ የአገልግሎት ሰጪ ማመልከቻ ግምገማ እየጠበቀ ነው።',
  'notify.open_admin': '📋 ገምግም',

  'agent.claimed': '🎉 እንኳን ደህና መጡ፣ {name}! አሁን የሃርመኒ ወኪል ነዎት።\n\nየወኪል ኮድዎ፦ {code}\n\nአገልግሎት ሰጪዎች ሲመዘገቡ ከእርስዎ ጋር እንዲገናኙ ይህን ሊንክ ያጋሩ፦\n{link}',
  'agent.claim_invalid': 'ይህ የወኪል ግብዣ ሊንክ ልክ ያልሆነ፣ ጊዜው ያለፈበት ወይም ቀደም ሲል ጥቅም ላይ የዋለ ነው። ከሃርመኒ አስተዳዳሪ አዲስ ይጠይቁ።',
  'agent.claim_conflict': 'ይህ የቴሌግራም መለያ እንደ ደንበኛ ወይም አገልግሎት ሰጪ ተመዝግቧል፣ ስለዚህ ወኪል መሆን አይችልም። ግብዣውን ከሌላ መለያ ይክፈቱ ወይም አስተዳዳሪውን ያነጋግሩ።',
  'agent.already': 'ቀደም ብለው የሃርመኒ ወኪል ነዎት። ኮድዎ፦ {code}',
  'test.reset_done': '🧪 የሙከራ ዳግም ማስጀመር ተጠናቋል። እንደገና ለመመዝገብ /start ይላኩ።',
  'test.reset_admin': '🧪 የአስተዳዳሪ መለያ ዳግም ሊጀመር አይችልም። ደንበኛ እና አገልግሎት ሰጪን ለመሞከር ሌላ የቴሌግራም መለያ ይጠቀሙ።',

  'common.back': 'ተመለስ',
  'common.cancel': 'ሰርዝ',
  'common.confirm': 'አረጋግጥ',
  'common.close': 'ዝጋ',
  'common.retry': 'እንደገና ሞክር',
  'common.load_more': 'ተጨማሪ አሳይ',
  'common.coming_soon': 'በቅርቡ ይመጣል',

  'feed.available_now': 'አሁን ዝግጁ',
  'feed.nearby': 'በአቅራቢያዎ',
  'feed.in_city': 'በከተማዎ',
  'feed.empty': 'አገልግሎት ሰጪ አልተገኘም። ርቀቱን ያስፉ ወይም ማጣሪያዎችን ይቀንሱ።',
  'feed.no_location': 'በቅርብ ያሉትን ለማየት በመገለጫ ውስጥ ቦታዎን ያጋሩ።',
  'distance.under1': 'ከ1 ኪ.ሜ በታች',
  'distance.km': '{km} ኪ.ሜ ርቀት',
  'filter.title': 'ማጣሪያዎች',
  'filter.distance': 'ርቀት',
  'filter.any_distance': 'ማንኛውም',
  'filter.gender': 'ጾታ',
  'filter.any': 'ማንኛውም',
  'filter.session': 'ቀጠሮ',
  'filter.max_price': 'ከፍተኛ ዋጋ (ብር)',
  'filter.rating': 'ደረጃ',
  'filter.available': 'አሁን ዝግጁ',
  'filter.clear': 'አጽዳ',
  'filter.apply': 'ውጤቶችን አሳይ',
  'filter.results': '{n} አገልግሎት ሰጪዎች',
  'provider.from_price': 'ከ{price} ብር ጀምሮ',
  'provider.new': 'አዲስ',
  'provider.book': 'ቀጠሮ ያዝ',
  'provider.prices': 'ዋጋዎች',
  'provider.about': 'ስለ',
  'provider.report': 'ሪፖርት አድርግ',
  'provider.block': 'አግድ',
  'provider.unblock': 'እገዳ አንሳ',
  'provider.favorite_add': 'ወደ ተወዳጆች ጨምር',
  'provider.favorite_remove': 'ከተወዳጆች አስወግድ',
  'provider.view_only': 'ቀጠሮ መያዝ የሚችሉት ደንበኞች ብቻ ናቸው።',
  'provider.unavailable': 'አሁን ዝግጁ አይደለም',
  'provider.available': 'ዝግጁ',
  'provider.age': '{age} ዓመት',
  'provider.min': '{minutes} ደቂቃ',
  'report.title': '{name}ን ሪፖርት አድርግ',
  'report.reason': 'ምን ተፈጠረ?',
  'report.details': 'ዝርዝር (አማራጭ)',
  'report.send': 'ሪፖርቱን ላክ',
  'report.sent': 'እናመሰግናለን። ቡድናችን ይገመግመዋል።',
  'report.cat.FAKE_PROFILE': 'የውሸት መገለጫ ወይም ፎቶ',
  'report.cat.INAPPROPRIATE': 'ተገቢ ያልሆነ ባህሪ',
  'report.cat.NO_SHOW': 'አልመጣም',
  'report.cat.RUDE': 'ባለጌ ወይም አክብሮት የጎደለው',
  'report.cat.SAFETY': 'ደህንነት አልተሰማኝም',
  'report.cat.SCAM': 'ከመተግበሪያው ውጭ ገንዘብ ጠየቀ',
  'report.cat.OTHER': 'ሌላ',
  'block.confirm': '{name}ን ማገድ ይፈልጋሉ? ዳግመኛ አያዩዋቸውም።',
  'block.done': 'ታግዷል',
  'block.unblocked': 'እገዳው ተነስቷል',
  'profile.favorites': 'ተወዳጆች',
  'profile.view_mine': 'ደንበኞች እንደሚያዩት መገለጫዬን እይ',
  'profile.blocked': 'የታገዱ አገልግሎት ሰጪዎች',
  'profile.location': 'የእኔ ቦታ',
  'profile.update_location': 'ቦታዬን አዘምን',
  'profile.location_updated': 'ቦታዎ ተዘምኗል',
  'favorites.empty': 'እስካሁን ተወዳጅ የለም። ለማስቀመጥ በአገልግሎት ሰጪው ላይ ያለውን ልብ ይንኩ።',
  'blocked.empty': 'ማንንም አላገዱም።',
  'availability.label': 'ጥያቄ ለመቀበል ዝግጁ',
  'availability.on': 'ደንበኞች ሊያዩዎት እና ጥያቄ ሊልኩልዎ ይችላሉ።',
  'availability.off': 'ተደብቀዋል። ጥያቄ ለመቀበል ይህን ያብሩ።',
  'availability.pending': 'መገለጫዎ ማጽደቅ እየጠበቀ ነው።',

  'book.title': '{name}ን ቀጠሮ ያዙ',
  'book.choose_session': 'የቀጠሮ ርዝመት ይምረጡ',
  'book.send': 'ጥያቄ ላክ',
  'book.sent_title': 'ጥያቄዎ ተልኳል',
  'book.sent_body': '{name} ለመመለስ {expiry} ደቂቃ አላቸው። ሲመልሱ በቴሌግራም መልዕክት ይደርስዎታል።',
  'book.credits_left': 'የቀሩ ጥያቄዎች፦ {n}',
  'book.no_credits_title': 'ጥያቄ አልቀረዎትም',
  'book.no_credits_body': 'በየ{hours} ሰዓቱ {quota} ነፃ ጥያቄዎች ያገኛሉ። ቀጣይ መሙያ፦ {time}።',
  'book.recharge': 'ተጨማሪ ጥያቄ ግዛ',
  'book.recharge_soon': 'ጥያቄ መግዛት በቅርቡ ይመጣል።',
  'book.keep_looking': 'መፈለግ ቀጥል',
  'book.pay_in_person': 'ሲገናኙ ለአገልግሎት ሰጪው በአካል ይከፍላሉ።',
  'bookings.active': 'ንቁ',
  'bookings.past': 'ያለፉ',
  'bookings.empty': 'እስካሁን ቀጠሮ የለም። በመነሻ ገጽ አገልግሎት ሰጪ ይፈልጉ።',
  'status.PENDING': 'መልስ በመጠበቅ ላይ',
  'status.ACCEPTED': 'ተቀብሏል',
  'status.DECLINED': 'ውድቅ ተደርጓል',
  'status.EXPIRED': 'ጊዜው አልፏል',
  'status.CANCELLED_BY_CLIENT': 'ተሰርዟል',
  'status.CANCELLED_BY_PROVIDER': 'በአገልግሎት ሰጪው ተሰርዟል',
  'status.COMPLETED': 'ተጠናቋል',
  'status.DISPUTED': 'አከራካሪ',
  'status.CLOSED': 'ተዘግቷል',
  'booking.cancel': 'ሰርዝ',
  'booking.cancel_title': 'ለምን ይሰርዛሉ?',
  'cancel.PRICE': 'ዋጋ',
  'cancel.CHANGED_MIND': 'ሀሳቤን ቀየርኩ',
  'cancel.PROVIDER_CANCELED': 'አገልግሎት ሰጪው ሰረዘ',
  'cancel.OTHER': 'ሌላ',
  'booking.open_chat': 'ውይይት ክፈት',
  'booking.expires_in': 'በ{m} ደቂቃ ውስጥ ይመልሱ',
  'booking.session': '{minutes} ደቂቃ · {price} ብር',
  'requests.empty': 'እስካሁን ጥያቄ የለም።',
  'requests.new': 'አዲስ ጥያቄዎች',
  'requests.accepted': 'የተቀበሏቸው',
  'requests.accept': 'ተቀበል',
  'requests.decline': 'ውድቅ አድርግ',
  'err.NO_CREDITS': 'ጥያቄ አልቀረዎትም።',
  'err.PROVIDER_UNAVAILABLE': 'ይህ አገልግሎት ሰጪ አሁን ዝግጁ አይደለም።',
  'err.ALREADY_REQUESTED': 'ከዚህ አገልግሎት ሰጪ ጋር መልስ የሚጠብቅ ጥያቄ አለዎት።',
  'err.REQUEST_EXPIRED': 'የዚህ ጥያቄ ጊዜ አልፏል።',
  'err.NOT_PENDING': 'ይህ ጥያቄ ቀደም ብሎ ተመልሷል።',
  'err.BLOCKED': 'ይህን አገልግሎት ሰጪ ቀጠሮ መያዝ አይችሉም።',
  'err.CLIENT_PROFILE_REQUIRED': 'መጀመሪያ መገለጫዎን ያጠናቅቁ።',
  'err.SESSION_NOT_OFFERED': 'ይህ አገልግሎት ሰጪ ያንን የቀጠሮ ርዝመት አይሰጥም።',
  'err.NOT_FOUND': 'አልተገኘም። ተወግዶ ሊሆን ይችላል።',
  'notify.new_request': '🔔 {name} ጥያቄ ልከውልዎታል፦ የ{minutes} ደቂቃ ቀጠሮ ({price} ብር)።\nእባክዎ በ{expiry} ደቂቃ ውስጥ ይመልሱ።',
  'notify.open_requests': '📥 ጥያቄውን እይ',
  'notify.request_accepted': '✅ {name} ጥያቄዎን ተቀብለዋል! አሁን ስለ ቀጠሮው መወያየት ይችላሉ።',
  'notify.request_declined': '❌ {name} ጥያቄዎን ውድቅ አድርገዋል።',
  'notify.request_expired': '⌛ ለ{name} የላኩት ጥያቄ ሳይመለስ ጊዜው አልፏል።',
  'notify.refund_line': 'የጥያቄ ክሬዲትዎ ተመልሷል።',
  'notify.cancelled_by_client': '🚫 {name} ጥያቄያቸውን ሰርዘዋል።',
  'notify.cancelled_by_provider': '🚫 {name} ቀጠሮዎን ሰርዘዋል።',
  'notify.open_chat': '💬 ውይይት ክፈት',
  'notify.open_bookings': '📋 የእኔ ቀጠሮዎች',
  'notify.find_another': '🔍 ሌላ አገልግሎት ሰጪ ፈልግ',

  'chat.empty_list': 'እስካሁን ውይይት የለም። አገልግሎት ሰጪ ጥያቄ ሲቀበል ውይይት ይከፈታል።',
  'chat.placeholder': 'መልዕክት',
  'chat.photo': 'ፎቶ',
  'chat.voice': 'የድምጽ መልዕክት',
  'chat.video': 'ቪዲዮ',
  'chat.recording': 'በመቅዳት ላይ… ለመላክ ■ ይንኩ',
  'chat.mic_denied': 'ማይክሮፎን አይገኝም። የማይክሮፎን ፈቃድ ይስጡ ወይም በጽሁፍ ይላኩ።',
  'chat.closed': 'ይህ ውይይት ተዘግቷል።',
  'chat.typing': 'እየጻፈ ነው…',
  'chat.failed': 'አልተላከም። እንደገና ለመሞከር ይንኩ።',
  'chat.start': 'ሰላም ይበሉ እና የት እና መቼ እንደምትገናኙ ይወስኑ።',
  'err.CHAT_CLOSED': 'ይህ ውይይት ተዘግቷል።',
  'err.CHAT_DISABLED': 'ውይይት አሁን ጠፍቷል።',
  'err.MEDIA_DISABLED': 'ፎቶ፣ ድምጽ እና ቪዲዮ አሁን ጠፍተዋል።',
  'notify.msg.TEXT': '💬 ከ{name} አዲስ መልዕክት',
  'notify.msg.PHOTO': '📷 {name} ፎቶ ልከውልዎታል',
  'notify.msg.VOICE': '🎤 {name} የድምጽ መልዕክት ልከውልዎታል',
  'notify.msg.VIDEO': '🎥 {name} ቪዲዮ ልከውልዎታል',

  // Phases 6–9
  'profile.wallet': 'የኪስ ቦርሳ',
  'wallet.title': 'የኪስ ቦርሳ',
  'wallet.requests_left': 'መላክ የሚችሏቸው ጥያቄዎች',
  'wallet.free': 'ነፃ',
  'wallet.paid': 'የተገዙ',
  'wallet.refill': 'ነፃ ጥያቄዎች የሚሞሉት {time}',
  'wallet.buy': 'ጥያቄዎችን ግዛ',
  'wallet.history': 'ታሪክ',
  'wallet.history_empty': 'እስካሁን ምንም እንቅስቃሴ የለም።',
  'wallet.payments': 'ክፍያዎች',
  'ledger.CREDIT_PURCHASE': 'ጥያቄዎች ተገዝተዋል',
  'ledger.CREDIT_SPEND': 'ጥያቄ ተልኳል',
  'ledger.CREDIT_RECHARGE': 'ነፃ መሙላት',
  'ledger.REFERRAL_REWARD': 'የግብዣ ሽልማት',
  'ledger.ADMIN_ADJUSTMENT': 'በሃርመኒ የተደረገ ማስተካከያ',
  'ledger.REFUND': 'ጥያቄ ተመልሷል',
  'pay.choose_package': 'ጥቅል ይምረጡ',
  'pay.package_line': '{credits} ጥያቄዎች',
  'pay.coupon': 'የኩፖን ኮድ',
  'pay.coupon_apply': 'ተግብር',
  'pay.coupon_ok': 'ኩፖን ተተግብሯል፦ −{amount} ብር',
  'pay.total': 'የሚከፈል ጠቅላላ',
  'pay.how_title': 'እንዴት እንደሚከፍሉ',
  'pay.instructions_default': 'ትክክለኛውን መጠን ቡድናችን ወደሰጠው የሃርመኒ ሂሳብ ይላኩ፣ ከዚያ የደረሰኙን ስክሪንሾት እዚህ ይጫኑ።',
  'pay.upload': 'የክፍያ ስክሪንሾት ይጫኑ',
  'pay.uploaded': 'ስክሪንሾት ታክሏል',
  'pay.submit': 'ለማጽደቅ ላክ',
  'pay.sent_title': 'ክፍያ ተልኳል',
  'pay.sent_body': 'ክፍያዎን አረጋግጠን ጥያቄዎቹን እንጨምራለን። የቴሌግራም መልዕክት ይደርስዎታል።',
  'pay.status.MANUAL_REVIEW': 'በመረጋገጥ ላይ',
  'pay.status.VERIFIED': 'ጸድቋል',
  'pay.status.DENIED': 'አልጸደቀም',
  'pay.status.PENDING': 'በመጠበቅ ላይ',
  'pay.status.INITIATED': 'ተጀምሯል',
  'pay.status.FAILED': 'አልተሳካም',
  'pay.status.EXPIRED': 'ጊዜው አልፏል',
  'pay.status.REFUNDED': 'ተመልሷል',
  'pay.disabled': 'ጥያቄ መግዛት አሁን ጠፍቷል።',
  'pay.no_packages': 'እስካሁን ምንም ጥቅል የለም።',
  'err.INVALID_COUPON': 'ይህ ኩፖን የሚሰራ አይደለም።',
  'err.COUPON_USED': 'ይህን ኩፖን አስቀድመው ተጠቅመዋል።',
  'err.PACKAGE_NOT_FOUND': 'ይህ ጥቅል ከአሁን በኋላ አይገኝም።',
  'err.EVIDENCE_REQUIRED': 'የክፍያዎን ስክሪንሾት ያክሉ።',
  'err.PAYMENT_DISABLED': 'ክፍያዎች አሁን ጠፍተዋል።',
  'err.TOO_MANY_PENDING': 'ማጽደቅ የሚጠብቁ ክፍያዎች አሉዎት።',
  'notify.payment_approved': '✅ ክፍያዎ ጸድቋል። {credits} ጥያቄዎች ወደ የኪስ ቦርሳዎ ተጨምረዋል።',
  'notify.payment_denied': '❌ ክፍያዎ አልጸደቀም።\nምክንያት፦ {reason}',
  'notify.subscription_paid': '✅ የደንበኝነት ክፍያዎ ጸድቋል። እስከ {date} ተከፍሏል።',
  'notify.view_balance': '👛 ቀሪ ሂሳብ እይ',
  'notify.new_payment': '💳 ከ{name} አዲስ ክፍያ፦ {amount} ብር ማጽደቅ እየጠበቀ ነው።',
  'myprofile.preview': 'ደንበኞች እርስዎን እንዲህ ያዩዎታል',
  'myprofile.settings': 'ቅንብሮች',
  'myprofile.inactive_by_agent': 'ወኪልዎ እንቅስቃሴ-አልባ አድርገውዎታል። ደንበኞች ሊያዩዎት አይችሉም።',
  'myprofile.inactive_by_admin': 'ሃርመኒ መገለጫዎን እንቅስቃሴ-አልባ አድርጓል። ድጋፍን ያግኙ።',
  'myprofile.inactive_self': 'መገለጫዎ እንቅስቃሴ-አልባ ነው። ደንበኞች ሊያዩዎት አይችሉም።',
  'myprofile.activate': 'አንቀሳቅሰኝ',
  'myprofile.activated': 'እንደገና ንቁ ሆነዋል',
  'myprofile.edit_requests': 'ከወኪልዎ የተጠቆሙ ለውጦች',
  'edit.field.displayName': 'ስም',
  'edit.field.bio': 'ስለ እርስዎ',
  'edit.field.prices': 'ዋጋዎች',
  'edit.accept': 'ተቀበል',
  'edit.reject': 'አትቀበል',
  'edit.accepted': 'ለውጦቹ ተቀምጠዋል',
  'edit.rejected': 'ጥቆማው ውድቅ ሆኗል',
  'edit.current': 'አሁን',
  'edit.suggested': 'የተጠቆመ',
  'notify.agent_set_inactive': '⏸ ወኪልዎ {agent} መገለጫዎን እንቅስቃሴ-አልባ አድርገዋል። ደንበኞች አሁን ሊያዩዎት አይችሉም። በመገለጫ ውስጥ መልሰው ማብራት ይችላሉ።',
  'notify.agent_set_active': '▶️ ወኪልዎ {agent} መገለጫዎን እንደገና ንቁ አድርገዋል።',
  'notify.edit_suggested': '✏️ ወኪልዎ {agent} በመገለጫዎ ላይ ለውጦችን ጠቁመዋል። ለመቀበል ወይም ላለመቀበል መገለጫን ይክፈቱ።',
  'notify.edit_accepted': '✅ {name} የጠቆሙትን ለውጦች ተቀብለዋል።',
  'notify.edit_rejected': '❌ {name} የጠቆሙትን ለውጦች አልተቀበሉም።',
  'notify.provider_reactivated': '▶️ {name} መገለጫቸውን መልሰው ንቁ አድርገዋል።',
  'notify.open_my_providers': '👥 የእኔ አገልግሎት ሰጪዎች',
  'agent.add_provider': 'አገልግሎት ሰጪ ያክሉ',
  'agent.add_hint': 'አገልግሎት ሰጪዎች ሲመዘገቡ ኮድዎን ያስገባሉ፣ ወይም የግብዣ ሊንክዎን ይከፍታሉ።',
  'agent.no_providers': 'እስካሁን አገልግሎት ሰጪ የለም። ለመጨመር ኮድዎን ወይም ሊንክዎን ያጋሩ።',
  'agent.stat.active': 'ንቁ አገልግሎት ሰጪዎች',
  'agent.stat.received': 'ጥያቄዎች',
  'agent.stat.accepted': 'ተቀባይነት ያገኙ',
  'agent.stat.declined': 'ውድቅ የሆኑ',
  'agent.stat.expired': 'ያመለጡ',
  'agent.stat.completed': 'የተጠናቀቁ',
  'agent.stat.cancelled': 'የተሰረዙ',
  'agent.stat.new': 'አዲስ አገልግሎት ሰጪዎች',
  'agent.period.today': 'ዛሬ',
  'agent.period.week': 'በዚህ ሳምንት',
  'agent.period.month': 'በዚህ ወር',
  'agent.period.last_month': 'ባለፈው ወር',
  'agent.pstatus.active': 'ንቁ',
  'agent.pstatus.inactive': 'እንቅስቃሴ-አልባ',
  'agent.pstatus.pending': 'ማጽደቅ በመጠበቅ ላይ',
  'agent.pstatus.denied': 'አልጸደቀም',
  'agent.pstatus.draft': 'ገና በመመዝገብ ላይ',
  'agent.set_active': 'ንቁ (ደንበኞች ያዩዋቸዋል)',
  'agent.suggest_edit': 'ለውጦችን ጠቁም',
  'agent.suggest_send': 'ጥቆማ ላክ',
  'agent.suggest_sent': 'ጥቆማው ለ{name} ተልኳል',
  'agent.suggest_pending': '{name} ለጥቆማዎ መልስ እስኪሰጡ በመጠበቅ ላይ',
  'agent.remove': 'ከዝርዝሬ አስወግድ',
  'agent.remove_confirm': '{name}ን ከዝርዝርዎ ያስወግዱ? ከእርስዎ ጋር እንደተገናኙ ይቆያሉ፤ ይህን መቀየር የሚችለው አስተዳዳሪ ብቻ ነው።',
  'agent.removed': 'ከዝርዝርዎ ተወግዷል',
  'agent.requests_count': 'በዚህ ወር {n} ጥያቄዎች',
  'agent.subscription_title': 'የደንበኝነት ክፍያ',
  'agent.subscription_body': '{count} አገልግሎት ሰጪዎች × {price} ብር = {amount} ብር በየ{days} ቀናት',
  'agent.subscription_pay': 'አሁን ክፈል',
  'agent.subscription_paid': 'እስከ {date} ተከፍሏል',
  'agent.subscription_due': 'ክፍያ ደርሷል',
  'agent.subscription_pending': 'ክፍያ ማጽደቅ በመጠበቅ ላይ',
  'agent.daily_report': '📊 የዕለት ሪፖርት — {date}\n\nንቁ አገልግሎት ሰጪዎች፦ {active}\nጥያቄዎች፦ {received}\nተቀባይነት ያገኙ፦ {accepted}\nውድቅ የሆኑ፦ {declined}\nያመለጡ፦ {expired}\nየተጠናቀቁ፦ {completed}\nአዲስ አገልግሎት ሰጪዎች፦ {new}',
  'err.NOT_YOUR_PROVIDER': 'ይህ አገልግሎት ሰጪ በዝርዝርዎ ውስጥ የለም።',
  'err.EDIT_PENDING': 'የሚጠብቅ ጥቆማ አለዎት።',
  'err.NOTHING_TO_CHANGE': 'ቢያንስ አንድ ነገር ይቀይሩ።',
  'err.INACTIVE_BY_ADMIN': 'ይህን መገለጫ መልሶ ማብራት የሚችለው ሃርመኒ ብቻ ነው።',
  'fb.ask': '💆 ከ{name} ጋር የነበረዎት የ{minutes} ደቂቃ ቀጠሮ እንዴት ነበር?',
  'fb.met': '✅ ተገናኘን',
  'fb.not_yet': '⏳ ገና አልተገናኘንም',
  'fb.canceled': '🚫 ሰርዘናል',
  'fb.rate': '{name}ን እንዴት ይገመግሙታል?',
  'fb.thanks': '🙏 ስለ ግምገማዎ እናመሰግናለን!',
  'fb.ask_later': 'እሺ፣ በኋላ እንደገና እንጠይቃለን።',
  'fb.no_more': 'እሺ፣ ከእንግዲህ አንጠይቅም።',
  'fb.cancel_reason': 'ለምን ተሰረዘ?',
  'fb.canceled_ok': 'ስላሳወቁን እናመሰግናለን።',
  'fb.already': 'ይህን አስቀድመው መልሰዋል።',
  'fb.card_title': 'ከ{name} ጋር ተገናኙ?',
  'fb.comment': 'አስተያየት ያክሉ (አማራጭ)',
  'fb.send': 'ግምገማ ላክ',
  'notify.new_rating': '⭐ {name} ቀጠሮዎን {rating}/5 ገምግመዋል።',
  'profile.invite': 'ጓደኞችን ይጋብዙ',
  'invite.title': 'ጓደኞችን ይጋብዙ',
  'invite.body': 'ሊንክዎን ያጋሩ። ጓደኛዎ ሲቀላቀል እና {event}፣ {credits} ነፃ ጥያቄዎች ያገኛሉ።',
  'invite.event.REGISTERED': 'ምዝገባውን ሲያጠናቅቅ',
  'invite.event.FIRST_BOOKING': 'የመጀመሪያ ጥያቄው ተቀባይነት ሲያገኝ',
  'invite.share': 'የግብዣ ሊንክ አጋራ',
  'invite.copy': 'ሊንክ ቅዳ',
  'invite.joined': 'የተቀላቀሉ ጓደኞች',
  'invite.earned': 'ያገኟቸው ጥያቄዎች',
  'invite.stats': '{joined} ተቀላቅለዋል · {rewarded} ተሸልመዋል',
  'invite.share_text': 'በሃርመኒ ይቀላቀሉኝ — በአቅራቢያዎ ያሉ የታመኑ የማሳጅ አገልግሎት ሰጪዎችን ያስይዙ፦',
  'invite.off': 'ግብዣዎች አሁን ጠፍተዋል።',
  'notify.referral_reward': '🎁 {name} በግብዣዎ ተቀላቅለዋል። {credits} ነፃ ጥያቄዎች አግኝተዋል!',
  'promo.use_code': 'ኮድ {code} ይጠቀሙ',
  'tut.skip': 'ዝለል',
  'tut.next': 'ቀጣይ',
  'tut.done': 'ጀምር',
  'checklist.title': 'መጀመሪያ ደረጃዎች',
  'checklist.progress': 'ከ{total} {done} ተጠናቋል',
  'profile.help': 'እገዛ እና ድጋፍ',
  'help.page_body': 'ጥያቄዎን በሃርመኒ ቦት ውይይት ውስጥ ይጻፉ፤ ቡድናችን እዚያው ይመልሳል።',
  'help.open_bot': 'ለድጋፍ ይጻፉ',
  'help.faq': 'ጥያቄዎች እና መልሶች',
  'support.received': '📨 እናመሰግናለን! መልዕክትዎ ለድጋፍ ቡድናችን ተልኳል። እዚሁ እንመልሳለን።',
  'support.reply': '💬 ከሃርመኒ ድጋፍ የተሰጠ መልስ፦\n\n{text}',
  'support.closed': '✅ የድጋፍ ጥያቄዎ ተዘግቷል። በማንኛውም ጊዜ አዲስ መልዕክት ይላኩ።',
  'notify.new_support': '💬 ከ{name} አዲስ የድጋፍ መልዕክት፦\n{text}',
  'admin.menu_title': '🛠 የአስተዳዳሪ ምናሌ',
  'admin.data': '📊 መረጃ',
  'admin.issues': '🚩 ጉዳዮች',
  'admin.broadcast': '📣 ማሰራጫ',
  'admin.users': '👥 ተጠቃሚዎችን አስተዳድር',
  'admin.payments': '💳 ክፍያዎች',
  'admin.support': '💬 ድጋፍ',
  'admin.summary': '📊 ዛሬ\n\nአዲስ ተጠቃሚዎች፦ {users}\nጥያቄዎች፦ {requests}\nተቀባይነት ያገኙ፦ {accepted}\nየሚጠብቁ ማጽደቆች፦ {approvals}\nክፍት ሪፖርቶች፦ {reports}\nየሚታዩ ክፍያዎች፦ {payments}\nየሚጠብቅ ድጋፍ፦ {support}',
  'notify.suspended': '⛔ የሃርመኒ መለያዎ ታግዷል። ስህተት ነው ብለው ካሰቡ ድጋፍን ያግኙ።',
  'notify.unsuspended': '✅ የሃርመኒ መለያዎ እንደገና ንቁ ነው።',
  'notify.photos_changed': '📷 {name} የመገለጫ ፎቶዎቻቸውን ቀይረዋል። አሁንም ከመታወቂያው ጋር እንደሚመሳሰሉ ያረጋግጡ።',
  'profile.edit': 'መገለጫ አስተካክል',
  'edit.title': 'መገለጫ አስተካክል',
  'edit.save': 'ለውጦችን አስቀምጥ',
  'edit.saved': 'መገለጫ ተቀምጧል',
  'edit.locked': 'ጾታ እና ዕድሜ ከመታወቂያዎ ጋር ተረጋግጠዋል። ለመቀየር ለድጋፍ ይጻፉ።',
  'edit.photos_hint': 'ከ1 እስከ 3 ፎቶዎች። የመጀመሪያው ቀድሞ ይታያል።',
};

export const STRINGS: Record<Language, Record<I18nKey, string>> = { en, am };

export function t(lang: Language | string | null | undefined, key: I18nKey, vars?: Record<string, string | number>): string {
  const dict = STRINGS[(lang === 'am' ? 'am' : 'en') as Language];
  let s: string = dict[key] ?? en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  }
  return s;
}
